/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useAssets, useCommon } from '@facephi/inphinite-common';
import {
  Dropdown,
  FlexContainer,
  Input,
  Label,
  useToast,
} from '@facephi/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { ModalIPStyles } from './Styles';
import {
  DESCRIPTION,
  RoutesName,
  RoutesNameAntifraud,
} from '../state/constants';
import { RulesModalDto, TypeRulesDto, sendRuleSchema } from '../state/model';
import { insertFace } from '../state/mutations/faces';

type Props = {
  show?: boolean;
  onChangeShow(show: boolean): void;
  className?: string;
};

export const ModalSendRule = ({ show, onChangeShow, className }: Props) => {
  const { t } = useTranslation();

  const { operation } = useCommon();
  const { getAssetToFile, uploadAsset } = useAssets();
  const [createFace] = useMutation(insertFace);
  const { toastManager } = useToast();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<RulesModalDto>({
    resolver: yupResolver(sendRuleSchema),
    mode: 'onChange',
  });

  const typeOptions = Object.keys(TypeRulesDto).map((item) => ({
    name: t(item),
    value: item,
  }));

  const onSubmit = async (newRule: RulesModalDto) => {
    if (newRule.type === TypeRulesDto.face) {
      const selphiAsset = operation?.assets.find(
        (item) => item.type === 'SELFIE',
      );

      if (selphiAsset) {
        const file = await getAssetToFile(selphiAsset.url, `${v4()}.jpg`);
        const response = await uploadAsset(file);
        await createFace({
          variables: {
            face: {
              media_id: response.id,
              description: newRule.description,
            },
          },
          onCompleted: () => {
            toastManager({
              type: 'success',
              message: t('Rule created successfully'),
              duration: 3000,
              testId: 'toast-create-rule',
            });
            navigate(RoutesName.rules, { state: { tab: 'face' } });
          },
        });

        onChangeShow(false);
      } else {
        toastManager({
          type: 'error',
          message: t('No selfie found in the operation'),
          duration: 3000,
          testId: 'toast-create-rule-error',
        });
      }
    }
  };

  useEffect(() => {
    reset({});
  }, [show]);

  return (
    <ModalIPStyles
      className={className}
      show={show}
      testId="modal-rule"
      header={{
        iconName: 'PlusCircle',
        title: t('Add an antifraud rule'),
      }}
      footer={{
        closeButtonLabel: t('close'),
        submitButtonLabel: t('Save'),
        onSubmitButton: handleSubmit(onSubmit),
      }}
      onChangeShow={onChangeShow}
      hasPadding
      isForm
    >
      <FlexContainer as="form" flexDirection="column" rowGap="1.6">
        <Label fontSize="14">{t('Choose rule type')}</Label>
        <Controller
          control={control}
          name="type"
          render={({ field, fieldState }) => (
            <Dropdown
              {...field}
              {...fieldState}
              testId="dropdown-type"
              options={typeOptions}
              label={t('Type')}
              placeholder={t('Choose type...')}
              overlay
              errorLabel={t(errors.type?.message as string)}
            />
          )}
        />
        <Controller
          name={DESCRIPTION}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              testId="face-input-desc"
              label={t('Description (optional)')}
              errorLabel={t(errors.description?.message as string)}
            />
          )}
        />
      </FlexContainer>
    </ModalIPStyles>
  );
};
